import { useGSAP } from "@gsap/react";
import { gsap } from "gsap/dist/gsap";
import React, { useId, useRef } from "react";

import { ValuePropsBlock as Value } from "@reactivated";

import { ButtonPrimary } from "../Clickables";
import { RichText } from "../RichText";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

interface Props {
    value: Value;
}

export const ValuePropsBlock = ({
    value: { header_subtitle, value_props, cta, theme },
}: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const introRef = useRef<HTMLDivElement>(null);
    const valuePropsRef = useRef<HTMLDivElement>(null);
    const valuePropsItemRefs = useRef<HTMLDivElement[]>([]);
    const id = useId();

    useGSAP(() => {
        if (
            !containerRef.current ||
            !introRef.current ||
            !valuePropsRef.current
        )
            return;

        const introHeight = introRef.current.offsetHeight;
        const valuePropsHeight = valuePropsRef.current.offsetHeight;
        const windowHeightHalf = window.innerHeight / 2;
        const marginTopInitial = `${((windowHeightHalf - introHeight / 2) / window.innerHeight) * 100}vh`;
        const marginTopEnd = `${((windowHeightHalf - (introHeight + valuePropsHeight) / 2) / window.innerHeight) * 100}vh`;

        gsap.set(introRef.current, { scale: 1.5, marginTop: marginTopInitial });

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: containerRef.current,
                start: "top top",
                end: "+=300%",
                scrub: 0.3,
                fastScrollEnd: true,
                pin: true,
            },
        });

        tl.to(
            introRef.current,
            {
                scale: 1,
                marginTop: marginTopEnd,
                ease: "expo.out",
                duration: 1,
            },
            0.3,
        )
            .fromTo(
                valuePropsRef.current,
                { opacity: 0 },
                { opacity: 1, ease: "expo.out", duration: 1 },
                0.3,
            )
            .fromTo(
                valuePropsItemRefs.current,
                { y: 100, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    ease: "expo.out",
                    duration: 0.5,
                    stagger: 0.05,
                },
                0.3,
            );
        return () => {
            tl.kill();
        };
    }, []);

    if (!value_props || value_props.length === 0) return <></>;

    return (
        <section ref={containerRef} className={styles[theme ?? "match"]}>
            <div className={styles.grid}>
                {header_subtitle && (
                    <div ref={introRef} className={styles.introContent}>
                        <Svg
                            name="logo-xcient-gradient"
                            className={styles.logo}
                        />
                        <RichText
                            value={header_subtitle}
                            className={styles.headerSubtitle}
                        />
                    </div>
                )}
                <div ref={valuePropsRef} className={styles.mainContent}>
                    <div className={styles.valueProps}>
                        {value_props.map(({ title, subtitle }, index) => {
                            if (!title && !subtitle) return;
                            const key = `prop-${id}-${index}`;

                            return (
                                <div
                                    className={styles.valueProp}
                                    key={key}
                                    ref={(element) => {
                                        if (element)
                                            valuePropsItemRefs.current[index] =
                                                element;
                                    }}
                                >
                                    {title && (
                                        <p className={styles.title}>{title}</p>
                                    )}
                                    {subtitle && (
                                        <p className={styles.subtitle}>
                                            {subtitle}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {cta && cta.url && (
                        <div className={styles.cta}>
                            <ButtonPrimary simpleLink={cta} icon="arrow" />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};
